import { zodResolver } from '@hookform/resolvers/zod'
import { IconCopy, IconDotsVertical, IconTrash } from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import type {
  EditableTableColumn,
  EditableTableFilter,
  EditableTableSummary,
} from '@/components/customComponents/scalrTables'
import { EditableTable } from '@/components/customComponents/scalrTables'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { TranslationFunctionType } from '@/i18n'
import {
  DEPARTMENTS,
  LOCATIONS,
  mockEmployees,
  POSITIONS,
} from '@/mocks/employeeData'
import { Employee, EmployeeRowData } from '@/types'
import { cn } from '@/utils'

// Helper function to create schema with translated messages
const createEmployeeSchema = (t: TranslationFunctionType) =>
  z.object({
    id: z.string(),
    name: z.string().min(1, t('scalrBase.masterLabor.validation.nameRequired')),
    position: z
      .string()
      .min(1, t('scalrBase.masterLabor.validation.positionRequired')),
    department: z
      .string()
      .min(1, t('scalrBase.masterLabor.validation.departmentRequired')),
    hourlyRate: z.coerce
      .number()
      .min(0, t('scalrBase.masterLabor.validation.hourlyRatePositive'))
      .max(1000, t('scalrBase.masterLabor.validation.hourlyRateTooHigh')),
    hoursWorked: z.coerce
      .number()
      .min(0, t('scalrBase.masterLabor.validation.hoursWorkedPositive'))
      .max(744, t('scalrBase.masterLabor.validation.hoursWorkedExceedsMax')),
    isActive: z.boolean(),
    startDate: z
      .string()
      .min(1, t('scalrBase.masterLabor.validation.startDateRequired')),
    email: z.string().email(t('scalrBase.masterLabor.validation.invalidEmail')),
    phone: z
      .string()
      .min(1, t('scalrBase.masterLabor.validation.phoneRequired')),
    employeeNumber: z
      .string()
      .min(1, t('scalrBase.masterLabor.validation.employeeNumberRequired')),
    location: z
      .string()
      .min(1, t('scalrBase.masterLabor.validation.locationRequired')),
  })

const createFormSchema = (t: TranslationFunctionType) =>
  z.object({
    employees: z.array(createEmployeeSchema(t)),
  })

type FormData = z.infer<ReturnType<typeof createFormSchema>>

const MasterLabor = () => {
  const { t } = useTranslation()
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedRows, setEditedRows] = useState<Set<string>>(new Set())
  const [deletedRows, setDeletedRows] = useState<Set<string>>(new Set())
  const [newRows, setNewRows] = useState<Set<string>>(new Set())
  const [employees, setEmployees] = useState<EmployeeRowData[]>(mockEmployees)

  // Filters
  const [searchText, setSearchText] = useState('')
  const [positionFilter, setPositionFilter] = useState<string>('all')
  const [departmentFilter, setDepartmentFilter] = useState<string>('all')
  const [locationFilter, setLocationFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  // Create schema with translations
  const formSchema = useMemo(() => createFormSchema(t), [t])

  const formMethods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      employees: employees,
    },
  })

  const { reset, watch, setValue, trigger } = formMethods

  // Update form when employees change
  useEffect(() => {
    reset({ employees })
  }, [employees, reset])

  // Watch for changes
  const watchedEmployees = watch('employees')

  // Track edited rows
  useEffect(() => {
    if (!isEditMode) return

    const edited = new Set<string>()
    watchedEmployees.forEach((employee, index) => {
      const original = employees[index]

      // Skip if it's a new row or deleted row
      if (newRows.has(employee.id) || deletedRows.has(employee.id)) {
        return
      }

      if (!original) {
        return
      }

      // Check if any field has changed (including number fields)
      const hasChanged = Object.keys(employee).some((key) => {
        const k = key as keyof Employee
        const currentValue = employee[k]
        const originalValue = original[k]

        // For numbers, convert to number and compare
        if (typeof originalValue === 'number') {
          return Number(currentValue) !== Number(originalValue)
        }

        return currentValue !== originalValue
      })

      if (hasChanged) {
        edited.add(employee.id)
      }
    })

    setEditedRows(edited)
  }, [watchedEmployees, employees, isEditMode, newRows, deletedRows])

  const handleEdit = useCallback(() => {
    setIsEditMode(true)
    setEditedRows(new Set())
    setDeletedRows(new Set())
    setNewRows(new Set())
  }, [])

  const handleCancel = useCallback(() => {
    setIsEditMode(false)
    setEditedRows(new Set())
    setDeletedRows(new Set())
    setNewRows(new Set())
    reset({ employees })
  }, [employees, reset])

  const handleAddRow = useCallback((): Employee => {
    const newEmployee: Employee = {
      id: `new-${Date.now()}`,
      name: '',
      position: '',
      department: '',
      hourlyRate: 0,
      hoursWorked: 0,
      isActive: true,
      startDate: new Date().toISOString().split('T')[0],
      email: '',
      phone: '',
      employeeNumber: '',
      location: '',
    }

    // Trigger validation for the new row after a short delay
    setTimeout(() => {
      trigger('employees')
    }, 100)

    return newEmployee
  }, [trigger])

  const handleCloneRow = useCallback(
    (rowIndex: number) => {
      const employeeToClone = watchedEmployees[rowIndex]
      const clonedEmployee: Employee = {
        ...employeeToClone,
        id: `new-${Date.now()}`,
        name: '', // Clear name when cloning
      }

      const newEmployees = [...watchedEmployees, clonedEmployee]
      setValue('employees', newEmployees)
      setNewRows((prev) => new Set(prev).add(clonedEmployee.id))
    },
    [watchedEmployees, setValue, setNewRows]
  )

  const calculateTotalCost = (hourlyRate: number, hoursWorked: number) => {
    return (hourlyRate * hoursWorked).toFixed(2)
  }

  const totalLabor = useMemo(() => {
    return employees
      .filter((emp) => !deletedRows.has(emp.id))
      .reduce((sum, emp) => sum + emp.hourlyRate * emp.hoursWorked, 0)
      .toFixed(2)
  }, [employees, deletedRows])

  const handleSave = useCallback(
    (data: {
      added: EmployeeRowData[]
      edited: EmployeeRowData[]
      deleted: EmployeeRowData[]
      all: EmployeeRowData[]
    }) => {
      console.log('=== Changes for Backend ===')
      console.log('Added:', data.added.length, 'rows')
      console.log(data.added)
      console.log('\nEdited:', data.edited.length, 'rows')
      console.log(data.edited)
      console.log('\nDeleted:', data.deleted.length, 'rows')
      console.log(data.deleted)
      console.log('===========================')

      // Update the employees state
      setEmployees(data.all)
      setIsEditMode(false)
      setEditedRows(new Set())
      setDeletedRows(new Set())
      setNewRows(new Set())
    },
    []
  )

  // Define table columns
  const columns: EditableTableColumn<EmployeeRowData>[] = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        minWidth: '200px',
        sticky: 'left',
        renderCell: ({ value }) => <span className="font-medium">{value}</span>,
        renderEditCell: ({ rowIndex, errors }) => (
          <div>
            <Input
              {...formMethods.register(`employees.${rowIndex}.name`)}
              placeholder="Enter name"
              className={cn(
                'h-9',
                errors?.employees?.[rowIndex]?.name && 'border-red-500'
              )}
            />
            {errors?.employees?.[rowIndex]?.name && (
              <p className="text-xs text-red-500 mt-1">
                {errors.employees[rowIndex]?.name?.message}
              </p>
            )}
          </div>
        ),
      },
      {
        header: 'Emp #',
        accessorKey: 'employeeNumber',
        minWidth: '150px',
        renderCell: ({ value }) => <span>{value}</span>,
        renderEditCell: ({ rowIndex, errors }) => (
          <div>
            <Input
              {...formMethods.register(`employees.${rowIndex}.employeeNumber`)}
              placeholder="EMP001"
              className={cn(
                'h-9',
                errors?.employees?.[rowIndex]?.employeeNumber &&
                  'border-red-500'
              )}
            />
            {errors?.employees?.[rowIndex]?.employeeNumber && (
              <p className="text-xs text-red-500 mt-1">
                {errors.employees[rowIndex]?.employeeNumber?.message}
              </p>
            )}
          </div>
        ),
      },
      {
        header: 'Position',
        accessorKey: 'position',
        minWidth: '200px',
        renderCell: ({ value }) => <span>{value}</span>,
        renderEditCell: ({ row, rowIndex, errors }) => (
          <div>
            <Select
              value={row.position || ''}
              onValueChange={(value) => {
                const newEmployees = [...watchedEmployees]
                newEmployees[rowIndex] = {
                  ...newEmployees[rowIndex],
                  position: value,
                }
                reset({ employees: newEmployees })
                setTimeout(() => trigger(`employees.${rowIndex}.position`), 50)
              }}
            >
              <SelectTrigger
                className={cn(
                  'h-9',
                  errors?.employees?.[rowIndex]?.position && 'border-red-500'
                )}
              >
                <SelectValue placeholder="Select position" />
              </SelectTrigger>
              <SelectContent>
                {POSITIONS.map((pos) => (
                  <SelectItem key={pos} value={pos}>
                    {pos}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors?.employees?.[rowIndex]?.position && (
              <p className="text-xs text-red-500 mt-1">
                {errors.employees[rowIndex]?.position?.message}
              </p>
            )}
          </div>
        ),
      },
      {
        header: 'Department',
        accessorKey: 'department',
        minWidth: '200px',
        renderCell: ({ value }) => <span>{value}</span>,
        renderEditCell: ({ row, rowIndex, errors }) => (
          <div>
            <Select
              value={row.department || ''}
              onValueChange={(value) => {
                const newEmployees = [...watchedEmployees]
                newEmployees[rowIndex] = {
                  ...newEmployees[rowIndex],
                  department: value,
                }
                reset({ employees: newEmployees })
                setTimeout(
                  () => trigger(`employees.${rowIndex}.department`),
                  50
                )
              }}
            >
              <SelectTrigger
                className={cn(
                  'h-9',
                  errors?.employees?.[rowIndex]?.department && 'border-red-500'
                )}
              >
                <SelectValue placeholder="Select department" />
              </SelectTrigger>
              <SelectContent>
                {DEPARTMENTS.map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors?.employees?.[rowIndex]?.department && (
              <p className="text-xs text-red-500 mt-1">
                {errors.employees[rowIndex]?.department?.message}
              </p>
            )}
          </div>
        ),
      },
      {
        header: 'Location',
        accessorKey: 'location',
        minWidth: '150px',
        renderCell: ({ value }) => <span>{value}</span>,
        renderEditCell: ({ row, rowIndex, errors }) => (
          <div>
            <Select
              value={row.location || ''}
              onValueChange={(value) => {
                const newEmployees = [...watchedEmployees]
                newEmployees[rowIndex] = {
                  ...newEmployees[rowIndex],
                  location: value,
                }
                reset({ employees: newEmployees })
                setTimeout(() => trigger(`employees.${rowIndex}.location`), 50)
              }}
            >
              <SelectTrigger
                className={cn(
                  'h-9',
                  errors?.employees?.[rowIndex]?.location && 'border-red-500'
                )}
              >
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {LOCATIONS.map((loc) => (
                  <SelectItem key={loc} value={loc}>
                    {loc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors?.employees?.[rowIndex]?.location && (
              <p className="text-xs text-red-500 mt-1">
                {errors.employees[rowIndex]?.location?.message}
              </p>
            )}
          </div>
        ),
      },
      {
        header: 'Hourly Rate',
        accessorKey: 'hourlyRate',
        minWidth: '150px',
        renderCell: ({ value }) => (
          <span>${(value as number)?.toFixed(2)}</span>
        ),
        renderEditCell: ({ rowIndex, errors }) => (
          <div>
            <Input
              {...formMethods.register(`employees.${rowIndex}.hourlyRate`)}
              type="number"
              step="0.01"
              placeholder="0.00"
              className={cn(
                'h-9',
                errors?.employees?.[rowIndex]?.hourlyRate && 'border-red-500'
              )}
            />
            {errors?.employees?.[rowIndex]?.hourlyRate && (
              <p className="text-xs text-red-500 mt-1">
                {errors.employees[rowIndex]?.hourlyRate?.message}
              </p>
            )}
          </div>
        ),
      },
      {
        header: 'Hours Worked',
        accessorKey: 'hoursWorked',
        minWidth: '150px',
        renderCell: ({ value }) => <span>{value}</span>,
        renderEditCell: ({ rowIndex, errors }) => (
          <div>
            <Input
              {...formMethods.register(`employees.${rowIndex}.hoursWorked`)}
              type="number"
              step="0.5"
              placeholder="0"
              className={cn(
                'h-9',
                errors?.employees?.[rowIndex]?.hoursWorked && 'border-red-500'
              )}
            />
            {errors?.employees?.[rowIndex]?.hoursWorked && (
              <p className="text-xs text-red-500 mt-1">
                {errors.employees[rowIndex]?.hoursWorked?.message}
              </p>
            )}
          </div>
        ),
      },
      {
        header: 'Total Cost',
        accessorKey: 'totalCost', // Changed to unique key
        minWidth: '150px',
        renderCell: ({ row }) => (
          <span className="font-semibold text-green-600">
            ${calculateTotalCost(row.hourlyRate || 0, row.hoursWorked || 0)}
          </span>
        ),
      },
      {
        header: 'Active',
        accessorKey: 'isActive',
        minWidth: '120px',
        renderCell: ({ value }) => (
          <div className="flex justify-center">
            {value ? (
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                Active
              </span>
            ) : (
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                Inactive
              </span>
            )}
          </div>
        ),
        renderEditCell: ({ row, rowIndex }) => (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={row.isActive}
              onCheckedChange={(checked) => {
                const newEmployees = [...watchedEmployees]
                newEmployees[rowIndex] = {
                  ...newEmployees[rowIndex],
                  isActive: checked as boolean,
                }
                reset({ employees: newEmployees })
              }}
            />
          </div>
        ),
      },
      {
        header: 'Start Date',
        accessorKey: 'startDate',
        minWidth: '180px',
        renderCell: ({ value }) => (
          <span>{new Date(value as string).toLocaleDateString()}</span>
        ),
        renderEditCell: ({ rowIndex, errors }) => (
          <div>
            <Input
              {...formMethods.register(`employees.${rowIndex}.startDate`)}
              type="date"
              className={cn(
                'h-9',
                errors?.employees?.[rowIndex]?.startDate && 'border-red-500'
              )}
            />
            {errors?.employees?.[rowIndex]?.startDate && (
              <p className="text-xs text-red-500 mt-1">
                {errors.employees[rowIndex]?.startDate?.message}
              </p>
            )}
          </div>
        ),
      },
      {
        header: 'Email',
        accessorKey: 'email',
        minWidth: '250px',
        renderCell: ({ value }) => (
          <span className="text-deepNavy-400">{value}</span>
        ),
        renderEditCell: ({ rowIndex, errors }) => (
          <div>
            <Input
              {...formMethods.register(`employees.${rowIndex}.email`)}
              type="email"
              placeholder="email@example.com"
              className={cn(
                'h-9',
                errors?.employees?.[rowIndex]?.email && 'border-red-500'
              )}
            />
            {errors?.employees?.[rowIndex]?.email && (
              <p className="text-xs text-red-500 mt-1">
                {errors.employees[rowIndex]?.email?.message}
              </p>
            )}
          </div>
        ),
      },
      {
        header: 'Phone',
        accessorKey: 'phone',
        minWidth: '200px',
        renderCell: ({ value }) => (
          <span className="text-deepNavy-400">{value}</span>
        ),
        renderEditCell: ({ rowIndex, errors }) => (
          <div>
            <Input
              {...formMethods.register(`employees.${rowIndex}.phone`)}
              placeholder="+1 (555) 123-4567"
              className={cn(
                'h-9',
                errors?.employees?.[rowIndex]?.phone && 'border-red-500'
              )}
            />
            {errors?.employees?.[rowIndex]?.phone && (
              <p className="text-xs text-red-500 mt-1">
                {errors.employees[rowIndex]?.phone?.message}
              </p>
            )}
          </div>
        ),
      },
    ],
    [formMethods, watchedEmployees, reset, trigger]
  )

  // Define filters
  const filters: EditableTableFilter[] = useMemo(
    () => [
      {
        type: 'search',
        placeholder: 'Search name, email, phone, employee #...',
        value: searchText,
        onChange: setSearchText,
      },
      {
        type: 'select',
        placeholder: 'All Positions',
        value: positionFilter,
        onChange: setPositionFilter,
        accessorKey: 'position',
        options: [
          { label: 'All Positions', value: 'all' },
          ...POSITIONS.map((pos) => ({ label: pos, value: pos })),
        ],
      },
      {
        type: 'select',
        placeholder: 'All Departments',
        value: departmentFilter,
        onChange: setDepartmentFilter,
        accessorKey: 'department',
        options: [
          { label: 'All Departments', value: 'all' },
          ...DEPARTMENTS.map((dept) => ({ label: dept, value: dept })),
        ],
      },
      {
        type: 'select',
        placeholder: 'All Locations',
        value: locationFilter,
        onChange: setLocationFilter,
        accessorKey: 'location',
        options: [
          { label: 'All Locations', value: 'all' },
          ...LOCATIONS.map((loc) => ({ label: loc, value: loc })),
        ],
      },
      {
        type: 'select',
        placeholder: 'All Status',
        value: statusFilter,
        onChange: setStatusFilter,
        customFilter: (row, value) => {
          if (value === 'active') return row.isActive === true
          if (value === 'inactive') return row.isActive === false
          return true
        },
        options: [
          { label: 'All Status', value: 'all' },
          { label: 'Active Only', value: 'active' },
          { label: 'Inactive Only', value: 'inactive' },
        ],
      },
    ],
    [searchText, positionFilter, departmentFilter, locationFilter, statusFilter]
  )

  // Define summary items
  const summaryItems: EditableTableSummary[] = useMemo(
    () => [
      {
        label: 'Total Employees',
        value: employees.filter((e) => !deletedRows.has(e.id)).length,
      },
      {
        label: 'Active Employees',
        value: employees.filter(
          (emp) => emp.isActive && !deletedRows.has(emp.id)
        ).length,
        className: 'text-green-600',
      },
      {
        label: 'Total Labor Cost',
        value: `$${totalLabor}`,
        className: 'text-blue-600',
      },
    ],
    [employees, deletedRows, totalLabor]
  )

  return (
    <EditableTable<EmployeeRowData>
      title="Employee Labor Management"
      description="Manage employee information, hours, and rates"
      formMethods={formMethods}
      formFieldName="employees"
      columns={columns}
      filters={filters}
      summaryItems={summaryItems}
      itemsPerPage={6}
      tableHeight={600}
      isEditMode={isEditMode}
      editedRows={editedRows}
      newRows={newRows}
      deletedRows={deletedRows}
      setEditedRows={setEditedRows}
      setNewRows={setNewRows}
      setDeletedRows={setDeletedRows}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
      onAddRow={handleAddRow}
      onCloneRow={handleCloneRow}
      showStatusIndicator={true}
      statusIndicatorWidth="10px"
      emptyMessage="No employees found"
      addButtonLabel="Add Employee"
      showDetailedErrors={true}
      getRowName={(row, index) => row.name || `Row ${index + 1}`}
      renderActions={({ onClone, onDelete }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
              <IconDotsVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onClone} className="cursor-pointer">
              <IconCopy className="h-4 w-4 mr-2" />
              Clone Row
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDelete}
              className="cursor-pointer text-red-600 focus:text-red-600"
            >
              <IconTrash className="h-4 w-4 mr-2" />
              Delete Row
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    />
  )
}

export default MasterLabor
